/**
 * CreativeLibraryTab — Grid of creatives for a business in the workspace.
 *
 * Shows all creatives saved to this business's library with status badges.
 * Clicking a card opens the detail sheet. "Generate new" CTA at top.
 */
import React, { useEffect, useState } from 'react';
import { Plus, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import CreativeCard from '@/components/deal/CreativeCard';
import CreativeDetailSheet from '@/components/deal/CreativeDetailSheet';

export default function CreativeLibraryTab({
  creatives = [],
  proposals = [],
  onGenerateNew,
  onGenerateAlternative,
  onArchive,
  onRenameCreative,
  onDeleteDraft,
  onUseInProposal,
  onEdit,
  onDuplicate,
  onUpdateStatus,
  generateDisabled = false,
  generateDisabledReason = '',
  onRetryGenerationUnlock,
}) {
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleCardClick = (creative) => {
    setSelectedCreative(creative);
    setSheetOpen(true);
  };

  useEffect(() => {
    if (!selectedCreative?.id) return;
    const nextSelectedCreative = creatives.find((creative) => creative.id === selectedCreative.id) || null;
    if (nextSelectedCreative) {
      setSelectedCreative(nextSelectedCreative);
      return;
    }
    setSelectedCreative(null);
    setSheetOpen(false);
  }, [creatives, selectedCreative?.id]);

  const handleArchive = (creative) => {
    onArchive?.(creative);
  };

  // Filter out archived by default, show active creatives
  const activeCreatives = creatives.filter((c) => c.status !== 'archived');
  const archivedCreatives = creatives.filter((c) => c.status === 'archived');

  if (creatives.length === 0) {
    return (
      <EmptyState
        icon={Video}
        title="No creatives yet"
        description={generateDisabledReason || 'Generate your first sample ad to pitch this business.'}
      >
        <Button
          onClick={onGenerateNew}
          disabled={generateDisabled}
          className="mt-2 bg-coral hover:bg-coral/90 text-white"
        >
          <Video className="h-4 w-4" />
          Generate creative
        </Button>
        {generateDisabled && onRetryGenerationUnlock ? (
          <Button variant="outline" onClick={onRetryGenerationUnlock} className="mt-2 bg-white">
            Retry profile build
          </Button>
        ) : null}
      </EmptyState>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-foreground">
            {activeCreatives.length} creative{activeCreatives.length !== 1 ? 's' : ''}
          </h3>
        </div>
        <Button
          size="sm"
          onClick={onGenerateNew}
          disabled={generateDisabled}
          className="bg-coral hover:bg-coral/90 text-white"
        >
          <Plus className="h-4 w-4" />
          Generate new
        </Button>
      </div>
      {generateDisabled && generateDisabledReason ? (
        <p className="text-sm text-slate-500">{generateDisabledReason}</p>
      ) : null}

      {/* Grid */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {activeCreatives.map((creative) => (
          <CreativeCard
            key={creative.id}
            creative={creative}
            onClick={handleCardClick}
            proposalCount={creative.proposalIds?.length || 0}
          />
        ))}
      </div>

      {/* Archived section */}
      {archivedCreatives.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Archived ({archivedCreatives.length})
          </h4>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 opacity-60">
            {archivedCreatives.map((creative) => (
              <CreativeCard
                key={creative.id}
                creative={creative}
                onClick={handleCardClick}
                proposalCount={creative.proposalIds?.length || 0}
              />
            ))}
          </div>
        </div>
      )}

      {/* Detail sheet */}
      <CreativeDetailSheet
        creative={selectedCreative}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onGenerateAlternative={onGenerateAlternative}
        onArchive={handleArchive}
        onRenameCreative={onRenameCreative}
        onDeleteDraft={onDeleteDraft}
        onUseInProposal={onUseInProposal}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        linkedProposals={
          selectedCreative?.proposalIds?.length
            ? proposals.filter((p) => selectedCreative.proposalIds.includes(p.id))
            : []
        }
      />
    </div>
  );
}
